import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const timestamp = z.string().datetime({ offset: false })
const base = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.number().int().nonnegative(),
}).strict()

export const estimateStateV1Schema = z.enum([
  "draft",
  "sent",
  "approved",
  "declined",
  "expired",
  "withdrawn",
  "superseded",
])

export const estimateDraftCreateV1Schema = base.extend({
  estimateId: entityIdSchema,
  revisionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  customerId: entityIdSchema,
  billingProfileId: entityIdSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  validUntil: timestamp.nullable(),
}).strict()

export const estimateReviseV1Schema = base.extend({
  estimateId: entityIdSchema,
  revisionId: entityIdSchema,
  previousRevisionId: entityIdSchema,
  validUntil: timestamp.nullable(),
  reason: z.string().trim().min(1).max(1_000),
}).strict()

export const estimateTransitionV1Schema = base.extend({
  estimateId: entityIdSchema,
  revisionId: entityIdSchema,
  targetState: estimateStateV1Schema.exclude(["draft"]),
  occurredAt: timestamp,
  reason: z.string().trim().min(1).max(1_000).nullable(),
}).strict()

export const estimateSendV1Schema = base.extend({
  commercialVersionId: entityIdSchema,
  revisionId: entityIdSchema,
  serviceLocationId: entityIdSchema,
  approverContactId: entityIdSchema,
  scope: z.string().trim().min(1).max(5_000),
  exclusions: z.array(z.string().trim().min(1).max(500)).max(50),
  assumptions: z.array(z.string().trim().min(1).max(500)).max(50),
  lineItems: z.array(z.object({
    serviceId: entityIdSchema,
    pricingSnapshotId: entityIdSchema,
  }).strict()).min(1).max(100),
  taxSnapshotId: entityIdSchema,
  paymentPolicyDecisionId: entityIdSchema,
  discountAmountMinor: z.number().int().nonnegative(),
  plannedVisitModel: z.enum(["single", "finite", "recurring"]),
  schedulePreferences: z.string().trim().min(1).max(2_000),
  billingMode: z.enum(["per_visit", "completion", "upfront", "milestone"]),
  validUntil: z.string().datetime({ offset: false }),
  sentAt: z.string().datetime({ offset: false }),
}).strict()

export const estimateSendResultV1Schema = z.object({
  commercialVersionId: entityIdSchema,
  ready: z.boolean(),
  blockers: z.array(z.enum([
    "service_location_not_estimatable",
    "estimate_approver_unavailable",
    "pricing_snapshot_invalid",
    "discount_exceeds_subtotal",
    "tax_quote_unresolved",
    "payment_policy_stale",
    "approver_limit_exceeded",
    "estimate_expiry_invalid",
  ])),
  warnings: z.array(z.string()),
  subtotalMinor: z.number().int().nonnegative(),
  discountAmountMinor: z.number().int().nonnegative(),
  taxAmountMinor: z.number().int().nonnegative().nullable(),
  totalAmountMinor: z.number().int().nonnegative().nullable(),
  paymentPath: z.string().nullable(),
  depositAmountMinor: z.number().int().nonnegative().nullable(),
  termsDays: z.number().int().nonnegative().nullable(),
}).passthrough()

export const estimateSummaryV1Schema = z.object({
  estimateId: entityIdSchema,
  recordVersion: z.number().int().positive(),
  revisionId: entityIdSchema,
  revisionNumber: z.number().int().positive(),
  state: estimateStateV1Schema,
}).strict()

export type EstimateStateV1 = z.infer<typeof estimateStateV1Schema>
export type EstimateDraftCreateV1 = z.infer<typeof estimateDraftCreateV1Schema>
export type EstimateReviseV1 = z.infer<typeof estimateReviseV1Schema>
export type EstimateTransitionV1 = z.infer<typeof estimateTransitionV1Schema>
export type EstimateSendV1 = z.infer<typeof estimateSendV1Schema>
export type EstimateSendResultV1 = z.infer<typeof estimateSendResultV1Schema>
export type EstimateSummaryV1 = z.infer<typeof estimateSummaryV1Schema>
